import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import { logger } from "@/utils/logger";

import "./App.scss";
import AppRouter from "./AppRouter";
import { AuthProvider } from "./context/AuthContext";

// Configure QueryClient with industry-standard defaults for responsive UI
// Key: Don't refetch on mount if data is fresh - this prevents blocking navigation
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Don't refetch on mount if data is still fresh - prevents blocking UI
      refetchOnMount: false, // Only refetch if data is stale
      refetchOnWindowFocus: false, // Don't refetch when window regains focus
      refetchOnReconnect: true, // Refetch when network reconnects (good for data freshness)
      // Use staleTime to determine when data is considered fresh
      staleTime: 1000 * 60 * 5, // 5 minutes - data is fresh for 5 minutes
      // Cache data for longer to improve performance
      gcTime: 1000 * 60 * 10, // 10 minutes (formerly cacheTime)
      // Retry configuration
      retry: 1, // Only retry once to avoid blocking
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      // Don't throw errors to UI - handle them gracefully
      throwOnError: false,
    },
  },
});

// Wrap App with Sentry Error Boundary
function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <div className="App">
        <AuthProvider>
          <AppRouter />
        </AuthProvider>
      </div>
    </QueryClientProvider>
  );
}

// Export wrapped with Sentry Error Boundary
export default Sentry.withErrorBoundary(App, {
  fallback: ({ error }) => {
    logger.error("React Error Boundary caught error", {
      error,
      context: "ErrorBoundary",
    });
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          padding: "2rem",
          textAlign: "center",
        }}
      >
        <h1>Something went wrong</h1>
        <p>We&apos;ve been notified and are working on a fix.</p>
        {process.env.NODE_ENV === "development" && (
          <pre
            style={{
              marginTop: "1rem",
              padding: "1rem",
              background: "#f5f5f5",
              borderRadius: "4px",
            }}
          >
            {error instanceof Error ? error.toString() : String(error)}
          </pre>
        )}
      </div>
    );
  },
  beforeCapture: (scope, error, errorInfo) => {
    logger.error("React Error Boundary caught error", {
      error,
      errorInfo,
      context: "ErrorBoundary",
    });
  },
});
